import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Wallet, QrCode, X, Check, Unplug, Smartphone, RefreshCw, Copy, Volume2 } from 'lucide-react';
import { BCHLogo } from './Layout';

// WizardConnect state managed externally since we dynamically load the hook
interface WCState {
  status: 'idle' | 'loading' | 'connecting' | 'connected' | 'disconnected' | 'error';
  uri: string | null;
  qrUri: string | null;
  walletName: string | null;
  walletIcon: string | null;
  error: string | null;
}

/**
 * WizardConnect settings panel — displayed inside Settings > Wallet.
 * Dynamically imports @wizardconnect/react's useWizardConnect + QR dialog.
 */
export function WizardConnectPanel() {
  const [wcState, setWcState] = useState<WCState>({
    status: 'idle', uri: null, qrUri: null,
    walletName: null, walletIcon: null, error: null,
  });
  const [copied, setCopied] = useState(false);

  // Hold refs to the dynamically loaded hook results
  const wcRef = useRef<any>(null);
  const moduleRef = useRef<any>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    // Check for existing session
    try {
      const saved = localStorage.getItem('wizardconnect-session');
      if (saved) {
        const session = JSON.parse(saved);
        if (session.walletName) {
          setWcState(prev => ({ ...prev, status: 'connected', walletName: session.walletName, walletIcon: session.walletIcon }));
        }
      }
    } catch {}
    return () => { mountedRef.current = false; };
  }, []);

  const handleConnect = useCallback(async () => {
    setWcState(prev => ({ ...prev, status: 'loading', error: null }));

    try {
      // Load the core + dapp modules
      if (!moduleRef.current) {
        const [coreMod, dappMod] = await Promise.all([
          import(/* @vite-ignore */ 'https://esm.sh/@wizardconnect/core@0.1.9?target=esnext'),
          import(/* @vite-ignore */ 'https://esm.sh/@wizardconnect/dapp@0.1.9?target=esnext'),
        ]);
        moduleRef.current = { core: coreMod, dapp: dappMod };
      }

      const { initiateDappRelay } = moduleRef.current.core;
      const { DappConnectionManager } = moduleRef.current.dapp;

      if (!initiateDappRelay || !DappConnectionManager) {
        throw new Error('WizardConnect SDK missing required exports');
      }

      // Create manager
      const manager = new DappConnectionManager('BCHNostr', window.location.origin + '/icons/icon-512x512.png', {
        session: { key: 'wizardconnect-session' },
      });

      // Listen for wallet ready
      manager.on('walletready', () => {
        if (!mountedRef.current) return;
        const walletName = manager.walletName || 'BCH Wallet';
        const walletIcon = manager.walletIcon || null;
        (globalThis as any).__wcManager = manager;
        setWcState(prev => ({ ...prev, status: 'connected', walletName, walletIcon }));
      });

      manager.on('disconnect', () => {
        if (!mountedRef.current) return;
        (globalThis as any).__wcManager = null;
        manager.clearStoredSession();
        setWcState(prev => ({ ...prev, status: 'disconnected', walletName: null, walletIcon: null, uri: null, qrUri: null }));
      });

      // Initiate relay connection
      const relay = initiateDappRelay((update: any) => {
        manager.updateConnection(update.client, update.status);
      }, {
        // No existing credentials — fresh connection
      });

      // Attach relay to manager for session persistence
      manager.attachRelay(relay);

      wcRef.current = { manager, relay };

      if (!mountedRef.current) return;

      setWcState(prev => ({
        ...prev,
        status: 'connecting',
        uri: relay.uri || null,
        qrUri: relay.qrUri || relay.uri || null,
      }));

    } catch (err) {
      console.error('[WizardConnect] Connection error:', err);
      if (!mountedRef.current) return;
      setWcState(prev => ({
        ...prev,
        status: 'error',
        error: err instanceof Error ? err.message : 'Failed to initialize WizardConnect',
      }));
    }
  }, []);

  const handleDisconnect = useCallback(async () => {
    try {
      if (wcRef.current?.manager) {
        await wcRef.current.manager.sendDisconnect().catch(() => {});
        wcRef.current.manager.clearStoredSession();
      }
      if (wcRef.current?.relay) {
        wcRef.current.relay.cleanup();
      }
    } catch {}
    wcRef.current = null;
    (globalThis as any).__wcManager = null;
    localStorage.removeItem('wizardconnect-session');
    setWcState({ status: 'idle', uri: null, qrUri: null, walletName: null, walletIcon: null, error: null });
  }, []);

  const handleCopyURI = () => {
    if (wcState.uri) {
      navigator.clipboard.writeText(wcState.uri).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(() => {});
    }
  };

  return (
    <div className="glass-card">
      <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
        <Smartphone className="w-4 h-4 text-primary" /> WizardConnect
      </h3>
      <p className="text-xs text-muted-foreground mb-4">
        Pair with Electron Cash or any WizardConnect-compatible BCH wallet to sign transactions directly from your wallet.
      </p>

      {/* ── Idle ── */}
      {(wcState.status === 'idle' || wcState.status === 'disconnected') && (
        <button
          onClick={handleConnect}
          className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 flex items-center justify-center gap-2"
        >
          <QrCode className="w-4 h-4" /> Connect Wallet
        </button>
      )}

      {/* ── Loading ── */}
      {wcState.status === 'loading' && (
        <div className="text-center py-4">
          <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-2" />
          <p className="text-xs text-muted-foreground">Loading WizardConnect...</p>
        </div>
      )}

      {/* ── Connecting (QR) ── */}
      {wcState.status === 'connecting' && (
        <div className="space-y-3 text-center">
          <p className="text-xs font-medium">Scan with your BCH wallet</p>
          {wcState.qrUri ? (
            <>
              <div className="p-3 bg-white rounded-2xl border shadow-sm inline-block">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(wcState.qrUri)}&ecc=H&margin=6`}
                  alt="WizardConnect QR"
                  width={200}
                  height={200}
                  className="rounded-lg"
                />
              </div>
              <div className="flex items-center gap-1 bg-muted rounded-lg p-2">
                <code className="flex-1 text-[9px] font-mono text-muted-foreground truncate text-left">{wcState.uri}</code>
                <button onClick={handleCopyURI} className="shrink-0 p-1 rounded hover:bg-background">
                  {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5 text-muted-foreground" />}
                </button>
              </div>
            </>
          ) : (
            <div className="py-4">
              <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">Generating connection...</p>
            </div>
          )}
          <div className="flex items-center justify-center gap-2">
            <div className="w-3 h-3 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
            <p className="text-[11px] text-muted-foreground">Waiting for wallet...</p>
          </div>
          <button onClick={handleDisconnect} className="text-xs text-muted-foreground hover:text-foreground">
            Cancel
          </button>
        </div>
      )}

      {/* ── Connected ── */}
      {wcState.status === 'connected' && (
        <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
            <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0">
              <Check className="w-5 h-5 text-emerald-500" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-emerald-600">Connected</p>
              <p className="text-xs text-muted-foreground truncate">{wcState.walletName || 'BCH Wallet'}</p>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Tips and payments will be signed by your connected wallet.
          </p>
          <button
            onClick={handleDisconnect}
            className="flex items-center justify-center gap-2 w-full py-2 rounded-lg border border-border hover:bg-muted text-sm text-muted-foreground hover:text-red-500"
          >
            <Unplug className="w-4 h-4" /> Disconnect
          </button>
        </div>
      )}

      {/* ── Error ── */}
      {wcState.status === 'error' && (
        <div className="space-y-3">
          <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-3">
            <p className="text-xs text-red-500 break-all">{wcState.error}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={handleConnect} className="flex-1 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium flex items-center justify-center gap-1.5">
              <RefreshCw className="w-3.5 h-3.5" /> Retry
            </button>
            <button onClick={() => setWcState(prev => ({ ...prev, status: 'idle', error: null }))} className="flex-1 py-2 rounded-lg border border-border text-sm hover:bg-muted">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

